import { createContext, useContext, useEffect, useState, ReactNode } from 'react';
import { User, Session } from '@supabase/supabase-js';
import { supabase } from '@/integrations/supabase/client';
import { clearAuthCaches } from '@/lib/pwa-cache';
import { toast } from 'sonner';
import { AppRole, withImplicitFan } from '@/config/roles';
import { fetchTeamManagerClub } from '@/hooks/useTeamManagerClub';
import { useQuery } from '@tanstack/react-query';

interface Profile {
  user_id: string;
  display_name: string | null;
  avatar_url: string | null;
  bio: string | null;
  date_of_birth: string | null;
  gender: string | null;
  nationality: string | null;
  strong_foot: string | null;
  is_verified: boolean;
}

interface UserRole {
  role: AppRole;
  status: string;
}

interface Club {
  id: string;
  club_name: string;
  owner_id: string;
}

interface AuthContextType {
  user: User | null;
  session: Session | null;
  profile: Profile | null;
  roles: AppRole[];
  activeRole: AppRole | null;
  roleAvatar: string | null;
  clubs: Club[];
  activeClub: Club | null;
  playerId: string | null;
  loading: boolean;
  clubsLoading: boolean;
  signUp: (email: string, password: string, displayName: string) => Promise<{ error: any }>;
  signIn: (email: string, password: string) => Promise<{ error: any }>;
  signOut: () => Promise<void>;
  switchRole: (role: AppRole, options?: { silent?: boolean }) => void;
  switchClub: (club: Club, options?: { silent?: boolean }) => void;
  refreshProfile: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }: { children: ReactNode }) => {
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [profile, setProfile] = useState<Profile | null>(null);
  const [roles, setRoles] = useState<AppRole[]>([]);
  const [activeRole, setActiveRole] = useState<AppRole | null>(null);
  const [roleAvatar, setRoleAvatar] = useState<string | null>(null);
  const [clubs, setClubs] = useState<Club[]>([]);
  const [activeClub, setActiveClub] = useState<Club | null>(null);
  const [playerId, setPlayerId] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [clubsLoading, setClubsLoading] = useState(false);

  const fetchProfileAndRoles = async (userId: string) => {
    try {
      const [profileResult, rolesResult] = await Promise.all([
        supabase
          .from('profiles')
          .select('*')
          .eq('user_id', userId)
          .maybeSingle(),
        supabase
          .from('user_roles')
          .select('role, status, is_primary_role, last_used_at')
          .eq('user_id', userId)
          .eq('status', 'active')
          .order('last_used_at', { ascending: false, nullsFirst: false }),
      ]);

      if (profileResult.error) throw profileResult.error;
      if (rolesResult.error) throw rolesResult.error;

      const profileData = profileResult.data;
      const rolesData = rolesResult.data ?? [];

      setProfile(profileData);
      const userRoles = withImplicitFan((rolesData as UserRole[]).map(r => r.role));
      setRoles(userRoles);

      // Determine active role with priority: admin roles > last used > primary > first available
      let defaultRole: AppRole | null = null;
      
      // First try: role from localStorage if still valid
      const storedRole = localStorage.getItem('activeRole') as AppRole;
      if (storedRole && userRoles.includes(storedRole)) {
        defaultRole = storedRole;
      }
      
      // Second try: prioritize admin and super_admin roles (they should never see fan interface)
      if (!defaultRole) {
        if (userRoles.includes('super_admin' as AppRole)) {
          defaultRole = 'super_admin' as AppRole;
        } else if (userRoles.includes('admin' as AppRole)) {
          defaultRole = 'admin' as AppRole;
        }
      }
      
      // Third try: most recently used role
      if (!defaultRole && rolesData && rolesData.length > 0) {
        const lastUsedRole = rolesData.find(r => r.last_used_at);
        if (lastUsedRole) {
          defaultRole = lastUsedRole.role;
        }
      }
      
      // Fourth try: primary role
      if (!defaultRole && rolesData) {
        const primaryRole = rolesData.find(r => r.is_primary_role);
        if (primaryRole) {
          defaultRole = primaryRole.role;
        }
      }
      
      // Last resort: first available role
      if (!defaultRole && userRoles.length > 0) {
        defaultRole = userRoles[0];
      }
      
      if (defaultRole) {
        setActiveRole(defaultRole);
        localStorage.setItem('activeRole', defaultRole);
        
        // Use profile avatar for all roles
        const avatarUrl = profileData?.avatar_url || null;
        setRoleAvatar(avatarUrl);
        
        // Clubs and the player profile are not required to leave the route
        // loader. Start them together and let pages wait on clubsLoading.
        const clubRelatedRoles: AppRole[] = ['club_owner', 'coach', 'club_admin', 'club_finance', 'club_manager', 'team_manager'];
        if (clubRelatedRoles.includes(defaultRole)) {
          void fetchUserClubs(userId, defaultRole);
        }
      }

      void fetchPlayerProfileId(userId);
    } catch (error: any) {
      console.error('Error fetching profile/roles:', error.message);
    }
  };

  // Platform settings for security features
  const { data: platformSettings } = useQuery({
    queryKey: ['platform-settings'],
    queryFn: async () => {
      const { data, error } = await supabase.from('platform_settings' as any).select('*');
      if (error) throw error;
      return (data as any)?.reduce((acc: any, s: any) => {
        acc[s.setting_key] = s.setting_value;
        return acc;
      }, {} as Record<string, any>) || {};
    },
    enabled: typeof window !== 'undefined',
  });

  useEffect(() => {
    const minutes = Number(platformSettings?.session_timeout || 0);
    if (!minutes || minutes <= 0) return;
    let timer: number | null = null;
    const reset = () => {
      if (timer) window.clearTimeout(timer);
      timer = window.setTimeout(async () => {
        await clearAuthCaches();
        await supabase.auth.signOut();
        toast.success('Session timed out due to inactivity');
      }, minutes * 60 * 1000);
    };
    const events = ['mousemove','keydown','click','touchstart'];
    events.forEach((e) => window.addEventListener(e, reset));
    reset();
    return () => {
      if (timer) window.clearTimeout(timer);
      events.forEach((e) => window.removeEventListener(e, reset));
    };
  }, [platformSettings?.session_timeout]);

  const fetchPlayerProfileId = async (userId: string) => {
    try {
      const { data: playerProfile, error } = await supabase
        .from('player_profiles')
        .select('id')
        .eq('user_id', userId)
        .maybeSingle();
      
      if (error) {
        // console.error('Error fetching player profile:', error.message);
        setPlayerId(null);
        return;
      }
      
      setPlayerId(playerProfile?.id ?? null);
    } catch (error: any) {
      // console.error('Error fetching player profile ID:', error.message);
      setPlayerId(null);
    }
  };

  const fetchUserClubs = async (userId: string, role: AppRole) => {
    setClubsLoading(true);
    try {
      // Only fetch clubs for club-related roles
      const staffRoles: AppRole[] = ['coach', 'club_admin', 'club_finance', 'club_manager', 'team_manager'];
      if (role !== 'club_owner' && !staffRoles.includes(role)) {
        setClubs([]);
        setActiveClub(null);
        localStorage.removeItem('activeClub');
        return;
      }

      let clubsData: Club[] = [];

      if (role === 'club_owner') {
        // Fetch clubs owned by the user
        const { data, error: clubsError } = await supabase
          .from('clubs')
          .select('id, club_name, owner_id')
          .eq('owner_id', userId)
          .order('club_name', { ascending: true });

        if (clubsError) throw clubsError;
        clubsData = data || [];
      } else if (role === 'team_manager') {
        // Same resolution as the team-manager dashboard (staff row, then team owner).
        const resolved = await fetchTeamManagerClub(userId);
        if (resolved) {
          const { data: clubRow, error: clubError } = await supabase
            .from('clubs')
            .select('id, club_name, owner_id')
            .eq('id', resolved.clubId)
            .maybeSingle();
          if (clubError) throw clubError;
          clubsData = clubRow
            ? [clubRow]
            : [{ id: resolved.clubId, club_name: resolved.clubName || 'Club', owner_id: userId }];
        }
      } else {
        // Fetch clubs where user is assigned as staff (coach, club_admin, club_finance, club_manager)
        const { data: staffAssignments, error: staffError } = await supabase
          .from('staff_assignments')
          .select(`
            club_id,
            clubs!inner(
              id,
              club_name,
              owner_id
            )
          `)
          .eq('role', role)
          .eq('user_id', userId)
          .or('status.eq.active,status.is.null');

        if (staffError) throw staffError;
        
        // Extract unique clubs from staff assignments
        const clubsMap = new Map<string, Club>();
        staffAssignments?.forEach((assignment: any) => {
          if (assignment.clubs) {
            clubsMap.set(assignment.clubs.id, {
              id: assignment.clubs.id,
              club_name: assignment.clubs.club_name,
              owner_id: assignment.clubs.owner_id
            });
          }
        });
        clubsData = Array.from(clubsMap.values()).sort((a, b) => 
          a.club_name.localeCompare(b.club_name)
        );
      }

      const userClubs = clubsData;
      setClubs(userClubs);

      // Set active club with priority: stored club > first available
      let defaultClub: Club | null = null;
      
      // First try: club from localStorage if still valid
      const storedClubId = localStorage.getItem('activeClub');
      if (storedClubId && userClubs.length > 0) {
        const storedClub = userClubs.find(club => club.id === storedClubId);
        if (storedClub) {
          defaultClub = storedClub;
        }
      }
      
      // Second try: first available club
      if (!defaultClub && userClubs.length > 0) {
        defaultClub = userClubs[0];
      }
      
      if (defaultClub) {
        setActiveClub(defaultClub);
        localStorage.setItem('activeClub', defaultClub.id);
      } else {
        setActiveClub(null);
        localStorage.removeItem('activeClub');
      }
    } catch (error: any) {
      console.error('Error fetching clubs:', error.message);
    } finally {
      setClubsLoading(false);
    }
  };

  useEffect(() => {
    let cancelled = false;

    // getSession restores the cold-load session. INITIAL_SESSION from the
    // listener would repeat that work, so later events only refresh state.
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      (event, currentSession) => {
        if (event === 'INITIAL_SESSION') return;

        setSession(currentSession);
        setUser(currentSession?.user ?? null);

        if (currentSession?.user) {
          void fetchProfileAndRoles(currentSession.user.id);
        } else {
          setProfile(null);
          setRoles([]);
          setActiveRole(null);
          setClubs([]);
          setActiveClub(null);
          setPlayerId(null);
          setClubsLoading(false);
          localStorage.removeItem('activeRole');
          localStorage.removeItem('activeClub');
          setLoading(false);
        }
      }
    );

    supabase.auth.getSession().then(({ data: { session: currentSession } }) => {
      if (cancelled) return;
      setSession(currentSession);
      setUser(currentSession?.user ?? null);

      if (currentSession?.user) {
        fetchProfileAndRoles(currentSession.user.id).finally(() => {
          if (!cancelled) setLoading(false);
        });
      } else {
        setLoading(false);
      }
    });

    return () => {
      cancelled = true;
      subscription.unsubscribe();
    };
  }, []);

  const signUp = async (email: string, password: string, displayName: string) => {
    const redirectUrl = `${window.location.origin}/dashboard`;

    const { error } = await supabase.auth.signUp({
      email,
      password,
      options: {
        emailRedirectTo: redirectUrl,
        data: {
          display_name: displayName
        }
      }
    });

    if (error) {
      toast.error(error.message);
    } else {
      toast.success('Account created! Check your email to verify.');
    }

    return { error };
  };

  const signIn = async (email: string, password: string) => {
    const { error } = await supabase.auth.signInWithPassword({
      email,
      password
    });

    if (error) {
      toast.error(error.message);
    } else {
      toast.success('Welcome back!');
    }

    return { error };
  };

  const signOut = async () => {
    await clearAuthCaches();
    await supabase.auth.signOut();
    localStorage.removeItem('activeRole');
    localStorage.removeItem('activeClub');
    toast.success('Signed out successfully');
  };

  const switchRole = async (role: AppRole, options?: { silent?: boolean }) => {
    if (roles.includes(role) && user) {
      setActiveRole(role);
      localStorage.setItem('activeRole', role);
      
      // Use profile avatar for all roles
      const avatarUrl = profile?.avatar_url || null;
      setRoleAvatar(avatarUrl);
      
      // Fetch clubs for club-related roles, clear for others
      await fetchUserClubs(user.id, role);
      
      if (!options?.silent) toast.success(`Switched to ${role} role`);
    }
  };

  const switchClub = (club: Club, options?: { silent?: boolean }) => {
    const clubRelatedRoles: AppRole[] = ['club_owner', 'coach', 'club_admin', 'club_finance', 'club_manager', 'team_manager'];
    if (clubs.includes(club) && activeRole && clubRelatedRoles.includes(activeRole)) {
      setActiveClub(club);
      localStorage.setItem('activeClub', club.id);
      if (!options?.silent) toast.success(`Switched to ${club.club_name}`);
    }
  };

  const refreshProfile = async () => {
    if (user) {
      await fetchProfileAndRoles(user.id);
    }
  };

  return (
    <AuthContext.Provider
      value={{
        user,
        session,
        profile,
        roles,
        activeRole,
        roleAvatar,
        clubs,
        activeClub,
        playerId,
        loading,
        clubsLoading,
        signUp,
        signIn,
        signOut,
        switchRole,
        switchClub,
        refreshProfile
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};
